import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, X, Check, CheckCheck, Trash2, Users, UserCog, 
  ChevronRight, AlertCircle, Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import useNotifications from '@/hooks/useNotifications';
import { haptics } from '@/utils/haptics';

/**
 * NotificationBell - Notification icon with badge for header
 */
export const NotificationBell = ({ onClick }) => {
  const { unreadCount } = useNotifications();
  
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      data-testid="notification-bell"
    >
      <Bell className="h-5 w-5 text-slate-600" />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

/**
 * NotificationPanel - Dropdown panel showing notifications
 */
export const NotificationPanel = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  } = useNotifications();

  if (!isOpen) return null;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'family_invitation':
      case 'family_accepted':
      case 'family_declined':
        return <Users className="h-5 w-5 text-purple-500" />;
      case 'caregiver_invitation':
      case 'caregiver_accepted':
      case 'caregiver_declined':
        return <UserCog className="h-5 w-5 text-teal-500" />;
      default:
        return <Bell className="h-5 w-5 text-slate-500" />;
    }
  };

  const handleNotificationClick = (notification) => {
    haptics.tap();
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-slate-500" />
            <span className="font-semibold text-slate-800">Notifications</span>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-red-100 text-red-600 text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  haptics.tap();
                  markAllAsRead();
                }}
                className="text-xs h-7 px-2"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Read all
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400 mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-10 w-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={cn(
                  "px-4 py-3 border-b border-slate-50 cursor-pointer transition-colors",
                  "hover:bg-slate-50",
                  !notification.read && "bg-blue-50/50"
                )}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        "text-sm",
                        !notification.read ? "font-semibold text-slate-800" : "text-slate-700"
                      )}>
                        {notification.title}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          haptics.tap();
                          deleteNotification(notification.id);
                        }}
                        className="text-slate-400 hover:text-red-500 p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTime(notification.created_at)}
                      </span>
                      {notification.action_url && (
                        <ChevronRight className="h-3 w-3 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                haptics.tap();
                clearAll();
              }}
              className="text-xs w-full text-slate-500 hover:text-red-500"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear all notifications
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * NotificationDropdown - Complete notification dropdown with bell and panel
 */
const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <NotificationBell onClick={() => setIsOpen(!isOpen)} />
      <NotificationPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  );
};

export default NotificationDropdown;
